import {Skeleton} from 'antd';
import React, {useEffect, useState} from 'react';

import {
  getOverviewInsight,
  type OverviewInsightKind,
  type OverviewInsightResult
} from '../../../../services/modules/ClimateDB/overviewInsightAPI';

import SustainabilityAiInsight from './SustainabilityAiInsight';

export const useOverviewInsight = (
  kind: OverviewInsightKind,
  selectedYear: string,
  refreshKey = false
): {
  status: 'loading' | 'ready' | 'hidden';
  result: OverviewInsightResult | null;
} => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'hidden'>(() =>
    selectedYear ? 'loading' : 'hidden'
  );
  const [result, setResult] = useState<OverviewInsightResult | null>(null);

  useEffect(() => {
    if (!selectedYear) {
      setStatus('hidden');
      setResult(null);

      return;
    }

    let isCurrent = true;

    setStatus('loading');
    getOverviewInsight(kind, selectedYear)
      .then((next) => {
        if (!isCurrent) {
          return;
        }

        setResult(next);
        setStatus(next.state === 'ready' ? 'ready' : 'hidden');
      })
      .catch(() => {
        if (isCurrent) {
          setResult({state: 'error'});
          setStatus('hidden');
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [kind, selectedYear, refreshKey]);

  return {status, result};
};

interface OverviewInsightPanelProps {
  status: 'loading' | 'ready' | 'hidden';
  result: OverviewInsightResult | null;
}

const OverviewInsightPanel: React.FC<OverviewInsightPanelProps> = ({
  status,
  result
}) => {
  const ready = result?.state === 'ready' ? result : null;

  if (status === 'hidden' || (status === 'ready' && !ready)) {
    return null;
  }

  if (status === 'loading' || !ready) {
    return (
      <aside className="yoySustainabilityInsight" aria-busy="true">
        <Skeleton active paragraph={{rows: 4}} title={false} />
      </aside>
    );
  }

  return (
    <SustainabilityAiInsight
      insight={{
        summary: ready.summary,
        factors: ready.factors,
        suggestion: ready.suggestion
      }}
    />
  );
};

export default OverviewInsightPanel;
